import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import '../../../utils/pdfFonts';
import { Product } from '../FolderView';

// Types
interface SerieGroup {
  name: string;
  count: number;
  products: Product[];
}

interface SerieGroupCluster {
  key: string;
  series: Record<string, SerieGroup>;
  totalCount: number;
}

interface HierarchyNode {
  name: string;
  count: number;
  children?: Record<string, HierarchyNode>;
  products?: Product[];
  series?: Record<string, SerieGroup>;
}

interface ProductKnowledgePDFProps {
  products: Product[];
  themeColors: { bg: string; border: string; text: string };
  brandName?: string;
}

// Helper functions (same as TreeView)
const getSerieKey = (productHierachy: string | undefined): string => {
  if (!productHierachy || productHierachy.length < 13) return "Unknown Serie";
  return productHierachy.substring(0, 13);
};

const getProductNamePrefix = (name: string | undefined): string => {
  if (!name) return "Unknown";
  const match = name.match(/^([^\d]+)/);
  return match ? match[1].trim() : "Unknown";
};

const getSerieGroupKey = (serieKey: string): string => {
  if (serieKey.length < 2) return serieKey;
  return serieKey.substring(serieKey.length - 1);
};

const groupSeriesByClusters = (series: Record<string, SerieGroup>): SerieGroupCluster[] => {
  const clusterMap: Record<string, SerieGroupCluster> = {};

  Object.entries(series).forEach(([serieKey, serieGroup]) => {
    const last2Chars = getSerieGroupKey(serieKey);
    const firstProduct = serieGroup.products[0];
    const namePrefix = getProductNamePrefix(firstProduct?.name);
    const clusterKey = `${last2Chars}|${namePrefix}`;

    if (!clusterMap[clusterKey]) {
      clusterMap[clusterKey] = {
        key: clusterKey,
        series: {},
        totalCount: 0,
      };
    }

    clusterMap[clusterKey].series[serieKey] = serieGroup;
    clusterMap[clusterKey].totalCount += serieGroup.count;
  });

  return Object.values(clusterMap).sort((a, b) => a.key.localeCompare(b.key));
};

const buildHierarchy = (products: Product[]): Record<string, HierarchyNode> => {
  const hierarchy: Record<string, HierarchyNode> = {};

  products.forEach((product) => {
    const category = product.category || "Uncategorized";
    const subcategory = product.subcategory || "General";
    const brandType = product.brandType || "Standard";
    const serieKey = getSerieKey(product.productHierachy);

    if (!hierarchy[category]) {
      hierarchy[category] = { name: category, count: 0, children: {} };
    }
    hierarchy[category].count++;

    if (!hierarchy[category].children![subcategory]) {
      hierarchy[category].children![subcategory] = { name: subcategory, count: 0, children: {} };
    }
    hierarchy[category].children![subcategory].count++;

    if (!hierarchy[category].children![subcategory].children![brandType]) {
      hierarchy[category].children![subcategory].children![brandType] = {
        name: brandType,
        count: 0,
        series: {},
      };
    }
    hierarchy[category].children![subcategory].children![brandType].count++;

    if (!hierarchy[category].children![subcategory].children![brandType].series![serieKey]) {
      hierarchy[category].children![subcategory].children![brandType].series![serieKey] = {
        name: serieKey,
        count: 0,
        products: [],
      };
    }
    hierarchy[category].children![subcategory].children![brandType].series![serieKey].count++;
    hierarchy[category].children![subcategory].children![brandType].series![serieKey].products.push(product);
  });

  return hierarchy;
};

// Styles
const styles = StyleSheet.create({
  // Common styles
  page: {
    padding: 30,
    fontFamily: 'Sarabun',
    fontSize: 10,
    backgroundColor: '#ffffff',
  },
  header: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: '#e5e7eb',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 11,
    color: '#6b7280',
  },
  statsRow: {
    flexDirection: 'row',
    gap: 15,
    marginTop: 10,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statLabel: {
    fontSize: 9,
    color: '#6b7280',
  },
  statValue: {
    fontSize: 10,
    fontWeight: 600,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#9ca3af',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 10,
  },

  // Category page styles
  subcategorySection: {
    width: '30%',
    marginBottom: 10,
    paddingLeft: 10,
    borderLeftWidth: 3,
  },
  subcategoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 6,
    backgroundColor: '#E0E7FF',
    borderRadius: 3,
    marginBottom: 4,
  },
  subcategoryTitle: {
    fontSize: 9,
    fontWeight: 600,
    color: '#3730A3',
  },
  subcategoryCount: {
    fontSize: 8,
    fontWeight: 600,
    color: '#3730A3',
  },
  brandTypeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 3,
    paddingHorizontal: 6,
    backgroundColor: '#D1FAE5',
    borderRadius: 3,
    marginLeft: 12,
    marginBottom: 3,
  },
  brandTypeItemText: {
    fontSize: 8,
    fontWeight: 500,
    color: '#065F46',
  },
  brandTypeItemCount: {
    fontSize: 7,
    color: '#047857',
  },
  categoryContent: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
  },

  // Detail page styles
  detailHeader: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 2,
  },
  detailTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 3,
  },
  detailSubtitle: {
    fontSize: 10,
    color: '#6b7280',
    marginBottom: 8,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 5,
  },
  breadcrumbText: {
    fontSize: 9,
    color: '#6b7280',
  },
  breadcrumbSeparator: {
    fontSize: 9,
    color: '#9ca3af',
  },

  // Group Series in detail page
  groupSeriesSection: {
    marginBottom: 4,
  },
  groupSeriesHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 3,
    paddingHorizontal: 6,
    backgroundColor: '#FEF3C7',
    borderRadius: 3,
    marginBottom: 2,
  },
  groupSeriesTitle: {
    fontSize: 9,
    fontWeight: 600,
    color: '#92400E',
  },
  groupSeriesCount: {
    fontSize: 8,
    color: '#B45309',
  },
  groupSeriesContent: {
    paddingLeft: 6,
  },

  // Series in detail page
  seriesSection: {
    marginBottom: 10,
    paddingLeft: 8,
    borderLeftWidth: 2,
    borderLeftColor: '#A78BFA',
  },
  seriesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: '#EDE9FE',
    borderRadius: 4,
    marginBottom: 8,
  },
  seriesTitle: {
    fontSize: 9,
    fontWeight: 600,
    color: '#5B21B6',
  },
  seriesCount: {
    fontSize: 8,
    fontWeight: 500,
    color: '#5B21B6',
  },

  // Products table styles
  table: {
    marginTop: 2,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
  },
  tableHeaderCell: {
    paddingVertical: 4,
    paddingHorizontal: 3,
    fontSize: 6,
    fontWeight: 700,
    color: '#374151',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
  },
  tableRowAlt: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
    backgroundColor: '#f9fafb',
  },
  tableCell: {
    paddingVertical: 3,
    paddingHorizontal: 3,
    fontSize: 5.5,
    color: '#4b5563',
  },
  brandTypeSection: {
    marginTop: 8,
    marginBottom: 4,
  },
  brandTypeSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 3,
    paddingHorizontal: 6,
    backgroundColor: '#D1FAE5',
    borderRadius: 3,
    marginBottom: 3,
  },
  brandTypeSectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: '#065F46',
  },
  brandTypeSectionCount: {
    fontSize: 8,
    color: '#047857',
  },
  seriesTableSection: {
    marginBottom: 4,
    paddingLeft: 6,
  },
  seriesTableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 2,
    paddingHorizontal: 6,
    backgroundColor: '#EDE9FE',
    borderRadius: 2,
    marginBottom: 2,
  },
  seriesTableTitle: {
    fontSize: 8,
    fontWeight: 600,
    color: '#5B21B6',
  },
  seriesTableCount: {
    fontSize: 7,
    color: '#7C3AED',
  },
});


// ============== PRODUCT TABLE COMPONENTS ==============

// Column widths (total ~100%)
const colWidths = {
  name: '14%',
  materialCode: '10%',
  barcode: '11%',
  productHierachy: '11%',
  titleEN: '14%',
  titleTH: '14%',
  status: '7%',
  functions: '10%',
  tags: '9%',
};

// Helper to parse JSON fields
const parseJsonField = (value: any): string => {
  if (!value) return '-';
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.join(', ');
      return String(parsed);
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

// Table Header
const ProductTableHeader = () => (
  <View style={styles.tableHeader} fixed>
    <Text style={[styles.tableHeaderCell, { width: colWidths.name }]}>Name</Text>
    <Text style={[styles.tableHeaderCell, { width: colWidths.materialCode }]}>Material Code</Text>
    <Text style={[styles.tableHeaderCell, { width: colWidths.barcode }]}>Barcode</Text>
    <Text style={[styles.tableHeaderCell, { width: colWidths.productHierachy }]}>Hierarchy</Text>
    <Text style={[styles.tableHeaderCell, { width: colWidths.titleEN }]}>Title EN</Text>
    <Text style={[styles.tableHeaderCell, { width: colWidths.titleTH }]}>Title TH</Text>
    <Text style={[styles.tableHeaderCell, { width: colWidths.status }]}>Status</Text>
    <Text style={[styles.tableHeaderCell, { width: colWidths.functions }]}>Functions</Text>
    <Text style={[styles.tableHeaderCell, { width: colWidths.tags }]}>Tags</Text>
  </View>
);

// Table Row
const ProductTableRow = ({ product, isAlt }: { product: Product; isAlt: boolean }) => (
  <View style={isAlt ? styles.tableRowAlt : styles.tableRow}>
    <Text style={[styles.tableCell, { width: colWidths.name }]}>{(product.name || '-').slice(0, 30)}</Text>
    <Text style={[styles.tableCell, { width: colWidths.materialCode }]}>{product.materialCode || '-'}</Text>
    <Text style={[styles.tableCell, { width: colWidths.barcode }]}>{product.barcode || '-'}</Text>
    <Text style={[styles.tableCell, { width: colWidths.productHierachy }]}>{product.productHierachy || '-'}</Text>
    <Text style={[styles.tableCell, { width: colWidths.titleEN }]}>{(product.titleEN || '-').slice(0, 25)}</Text>
    <Text style={[styles.tableCell, { width: colWidths.titleTH }]}>{(product.titleTH || '-').slice(0, 25)}</Text>
    <Text style={[styles.tableCell, { width: colWidths.status }]}>{product.status || '-'}</Text>
    <Text style={[styles.tableCell, { width: colWidths.functions }]}>{parseJsonField(product.functions).slice(0, 20)}</Text>
    <Text style={[styles.tableCell, { width: colWidths.tags }]}>{parseJsonField(product.tags).slice(0, 20)}</Text>
  </View>
);

// Series table with products
const SeriesTablePDF = ({ serieKey, serieGroup }: { serieKey: string; serieGroup: SerieGroup }) => (
  <View style={styles.seriesTableSection}>
    <View style={styles.seriesTableHeader} minPresenceAhead={40}>
      <Text style={styles.seriesTableTitle}>{serieKey}</Text>
      <Text style={styles.seriesTableCount}>({serieGroup.count} products)</Text>
    </View>
    <View style={styles.table}>
      <ProductTableHeader />
      {serieGroup.products.map((product, idx) => (
        <ProductTableRow key={product.id} product={product} isAlt={idx % 2 === 1} />
      ))}
    </View>
  </View>
);

// Group Series section with series tables inside
const GroupSeriesTableSection = ({ cluster }: { cluster: SerieGroupCluster }) => {
  const sortedSeries = Object.entries(cluster.series).sort(([a], [b]) => a.localeCompare(b));
  const groupName = cluster.key.split("|")[1] || "Unknown";

  return (
    <View style={styles.groupSeriesSection}>
      <View style={styles.groupSeriesHeader} minPresenceAhead={60}>
        <Text style={styles.groupSeriesTitle}>{groupName}</Text>
        <Text style={styles.groupSeriesCount}>({cluster.totalCount} products)</Text>
      </View>
      <View style={styles.groupSeriesContent}>
        {sortedSeries.map(([serieKey, serieGroup]) => (
          <SeriesTablePDF key={serieKey} serieKey={serieKey} serieGroup={serieGroup} />
        ))}
      </View>
    </View>
  );
};

// BrandType section with group series tables
const BrandTypeTableSection = ({ btKey, btNode }: { btKey: string; btNode: HierarchyNode }) => {
  const clusters = btNode.series ? groupSeriesByClusters(btNode.series) : [];

  return (
    <View style={styles.brandTypeSection}>
      <View style={styles.brandTypeSectionHeader} minPresenceAhead={60}>
        <Text style={styles.brandTypeSectionTitle}>{btKey}</Text>
        {/* <Text style={styles.brandTypeSectionCount}>({btNode.count} products)</Text> */}
      </View>
      {clusters.map((cluster) => (
        <GroupSeriesTableSection key={cluster.key} cluster={cluster} />
      ))}
    </View>
  );
};

// ============== CATEGORY PAGE COMPONENTS ==============

// BrandType item for category page (compact)
const BrandTypeItemPDF = ({ btKey, btNode }: { btKey: string; btNode: HierarchyNode }) => (
  <View style={styles.brandTypeItem}>
    <Text style={styles.brandTypeItemText}>{btKey}</Text>
    <Text style={styles.brandTypeItemCount}>({btNode.count})</Text>
  </View>
);

// Subcategory section within category page
const SubcategoryPDF = ({ subKey, subNode }: {
  subKey: string;
  subNode: HierarchyNode;
}) => {
  const sortedBrandTypes = Object.entries(subNode.children || {}).sort(([a], [b]) => a.localeCompare(b));

  return (
    <View style={[styles.subcategorySection, { borderLeftColor: '#6366F1' }]}>
      <View style={styles.subcategoryHeader}>
        <Text style={styles.subcategoryTitle}>{subKey}</Text>
        <Text style={styles.subcategoryCount}>{subNode.count} products</Text>
      </View>
      {sortedBrandTypes.map(([btKey, btNode]) => (
        <BrandTypeItemPDF key={btKey} btKey={btKey} btNode={btNode} />
      ))}
    </View>
  );
};

// Category page - 1 Category per A4 landscape page
const CategoryPage = ({
  categoryKey,
  categoryNode,
  themeColors,
  currentDate,
  brandName,
}: {
  categoryKey: string;
  categoryNode: HierarchyNode;
  themeColors: { bg: string; border: string; text: string };
  currentDate: string;
  brandName: string;
}) => {
  const sortedSubcategories = Object.entries(categoryNode.children || {}).sort(([a], [b]) => a.localeCompare(b));

  // Count subcategories and brandTypes for this category
  let subcategoriesCount = sortedSubcategories.length;
  let brandTypesCount = 0;
  sortedSubcategories.forEach(([, sub]) => {
    if (sub.children) {
      brandTypesCount += Object.keys(sub.children).length;
    }
  });

  // Collect all BrandTypes for this category
  const allBrandTypes: { subKey: string; btKey: string; btNode: HierarchyNode }[] = [];
  sortedSubcategories.forEach(([subKey, sub]) => {
    if (sub.children) {
      Object.entries(sub.children).sort(([a], [b]) => a.localeCompare(b)).forEach(([btKey, btNode]) => {
        allBrandTypes.push({ subKey, btKey, btNode });
      });
    }
  });

  return (
    <Page size="A4" orientation="landscape" style={styles.page} wrap>
      {/* Header - fixed to repeat on all pages */}
      <View style={[styles.header, { borderBottomColor: themeColors.border }]} fixed>
        <Text style={[styles.title, { color: themeColors.text }]}>
          {categoryKey}
        </Text>
        <Text style={styles.subtitle}>
          Product Knowledge - {brandName}
        </Text>
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <Text style={styles.statLabel}>Sub-categories:</Text>
            <Text style={styles.statValue}>{subcategoriesCount}</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statLabel}>Brand Types:</Text>
            <Text style={styles.statValue}>{brandTypesCount}</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statLabel}>Products:</Text>
            <Text style={[styles.statValue, { color: themeColors.text }]}>{categoryNode.count}</Text>
          </View>
        </View>
      </View>

      {/* Overview: Subcategories and BrandTypes */}
      <View style={styles.categoryContent}>
        {sortedSubcategories.map(([subKey, subNode]) => (
          <SubcategoryPDF key={subKey} subKey={subKey} subNode={subNode} />
        ))}
      </View>

      {/* Product Tables by BrandType and Series */}
      {allBrandTypes.map(({ subKey, btKey, btNode }) => (
        <BrandTypeTableSection key={`${subKey}-${btKey}`} btKey={btKey} btNode={btNode} />
      ))}
    </Page>
  );
};

// ============== MAIN PDF DOCUMENT ==============

const ProductKnowledgePDF = ({ products, themeColors, brandName = "Brand" }: ProductKnowledgePDFProps) => {
  const hierarchy = buildHierarchy(products);
  const sortedCategories = Object.entries(hierarchy).sort(([a], [b]) => a.localeCompare(b));

  const currentDate = new Date().toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Document>
      {/* Category pages - 1 Category per A4 landscape page */}
      {sortedCategories.map(([categoryKey, categoryNode]) => (
        <CategoryPage
          key={categoryKey}
          categoryKey={categoryKey}
          categoryNode={categoryNode}
          themeColors={themeColors}
          currentDate={currentDate}
          brandName={brandName}
        />
      ))}
    </Document>
  );
};

export default ProductKnowledgePDF;
